import ko from 'knockout';
import _ from 'underscore';

/**
 * Generic Multi level dropdown which can be used across bingads platform.
 * @param {array} data - data to be shown in the dropdown. data object
 * will need to follow below format
 * [{
 * children: null,
 * text: 'test1',
 * value: 'value1'
 *}, {
 * children: {
 *   children: null,
 *   text: 'test2.1',
 *   value: 'value2.1'
 * },
 *   text: 'test2',
 *   value: 'value2'
 * }]

 * @param {string} preset - the default value of dropdown. This value must match
 * one of the "value" properties
 * in data object passed above. If not found, it will throw an exception
 * @param {string} defaultBlankMenuText - if this value exists, the dropdown will
 * display empty option with provided text. This option will be overwritten if there
 * is a preset value
 * @param {bool} disableParentClick - If this property is true, parent element click will not
 * trigger any action.
 */

export default class MultilevelDropdownViewModel {
  constructor({
    data,
    preset,
    customTemplate,
    isMultiSelect,
    disableParentClick,
    defaultBlankMenuText,
  }) {
    this.validateInput(data);
    this.currentSelection = isMultiSelect ? ko.observableArray() : ko.observable();
    this.data = data;
    this.preset = preset;
    this.customTemplate = customTemplate;
    this.isMultiSelect = isMultiSelect;
    this.disableParentClick = disableParentClick;
    this.defaultBlankMenuText = defaultBlankMenuText;
    this.isCustomTemplate = ko.observable(!_.isUndefined(customTemplate));
    this.initializeData(data);
  }

  initializeData(data, parent) {
    if (this.preset && this.defaultBlankMenuText) {
      throw new Error('preset and default blank menu text cannot co-exist!');
    }
    return _.map(data, (obj) => {
      /* eslint-disable no-param-reassign */
      obj.parent = ko.observable(parent || null);
      /* eslint-disable no-param-reassign */
      obj.showChildren = ko.observable(false);
      /* eslint-disable no-param-reassign */
      obj.selected = ko.observable(false);

      if (obj.children && obj.children.length > 0) {
        obj.children = this.initializeData(obj.children, obj);
      }
      return obj;
    });
  }

  triggerPresetValue() {
    // if preset value does not exist, or preset value exists but cannot be found
    // if default blank menu text exists, do not select anything
    // else select very first element
    if (!(this.preset && this.selectPresetValue(this.data, this.preset)) &&
      !this.defaultBlankMenuText) {
      this.select(this.setDefaultValue(this.data).value);
    }
  }

  validateInput(data) {
    // check if text and values are strings
    const hashTable = {};
    _.each(data, (entry) => {
      if (_.isNull(entry) || _.isUndefined(entry)) {
        throw new Error('Entry inside data object cannot be null');
      } else if (!_.isString(entry.text)) {
        throw new Error('text must be string type');
      }

      // check if there is any duplicative value;
      if (_.isUndefined(hashTable[entry.value])) {
        hashTable[entry.value] = true;
      } else {
        throw new Error('value must be unique!');
      }
    });
  }

  update(value, object) {
    const node = this.findNode(this.data, value);

    node.text = object.text;
    node.divider = object.divider;
    node.disabled = object.disabled;
    node.hide = object.hide;

    return node;
  }

  add(parentValue, object) {
    if (!parentValue) {
      this.data.push(object);
    } else {
      const node = this.findNode(this.data, parentValue);

      if (!node) {
        throw new Error('Parent value does not exists!');
      }

      if (node.children) {
        node.children.push(object);
      } else {
        node.children = [object];
      }
    }
  }

  delete(value) {
    const node = this.findNode(this.data, value);

    if (node.parent()) {
      const index = this.data.indexOf(node);

      this.data.splice(index, 1);
    } else {
      const parent = node.parent();
      const index = parent.children.indexOf(node);

      parent.children.splice(index, 1);
    }
  }

  showSubmenu(value) {
    const node = this.findNode(this.data, value);
    this.hideSubmenu(node);
    node.showChildren(true);
  }

  hideSubmenu(data) {
    const parent = data.parent();
    // if parent === null, we are at root
    if (parent === null) {
      _.each(this.data, (child) => {
        child.showChildren(false);
      });
    } else if (typeof (parent) === 'object') {
      _.each(parent.children, (child) => {
        child.showChildren(false);
      });
    }
  }

  propagateValueToChildren(node) {
    _.each(node.children, (child) => {
      child.selected(node.selected());
      if (child.children && child.children.length > 0) {
        this.propagateValueToChildren(child);
      }
    });
  }

  select(data) {
    let node = this.findNode(this.data, data);
    let parentNode = node.parent();

    if (node.children && node.children.length > 0 && this.disableParentClick) {
      return;
    }

    if (this.isMultiSelect) {
      node.selected(!node.selected());

      // check/uncheck all the children as well
      this.propagateValueToChildren(node);

      while (parentNode !== null) {
        // if any child is selected
        parentNode.selected(false);
        for (let i = 0; parentNode.children && i < parentNode.children.length; i += 1) {
          if (parentNode.children[i] && parentNode.children[i].selected()) {
            parentNode.selected(true);
            break;
          }
        }
        parentNode = parentNode.parent();
      }

      const index = this.currentSelection().indexOf(node);
      if (index > -1) {
        this.currentSelection.remove(item => item.value === node.value);
      } else {
        this.currentSelection.push(node);
      }
    } else {
      if (this.currentSelection()) {
        let prevSelectedNode = this.findNode(this.data, this.currentSelection().value);
        // delete selected flag of previously selected node
        prevSelectedNode.selected(false);
        while (prevSelectedNode.parent()) {
          prevSelectedNode.parent().selected(false);
          prevSelectedNode = prevSelectedNode.parent();
        }
      }

      node.selected(true);
      this.currentSelection(node);
      while (node.parent()) {
        node.parent().selected(true);
        node = node.parent();
      }
    }
  }

  isVisible(value) {
    const node = this.findNode(this.data, value);

    return ko.computed(() => node.showChildren());
  }

  selectPresetValue(list, value) {
    if (this.isMultiSelect && Array.isArray(value)) {
      let isAnythingSelected = false;
      for (let i = 0; i < list.length; i += 1) {
        if (value.indexOf(list[i].value) > -1) {
          this.select(list[i].value);
          isAnythingSelected = true;
        }
        if (list[i].children) {
          isAnythingSelected = isAnythingSelected ||
          this.selectPresetValue(list[i].children, value);
        }
      }
      return isAnythingSelected;
    }

    for (let i = 0; i < list.length; i += 1) {
      if (list[i].value === value) {
        this.select(list[i].value);
        return true;
      }
      if (list[i].children && this.selectPresetValue(list[i].children, value)) {
        return true;
      }
    }
    return false;
  }

  findNode(list, value) {
    if (!list) {
      return null;
    }

    for (let i = 0; i < list.length; i += 1) {
      if (list[i].value === value || list[i].value === parseInt(value, 10)) {
        return list[i];
      }
      const node = this.findNode(list[i].children, value);

      if (list[i].children && list[i].children.length > 0 && node) {
        return node;
      }
    }
    return null;
  }

  setDefaultValue(list) {
    if (list.length > 0) {
      let tmpNode = list[0];
      while (tmpNode.children && tmpNode.children.length > 0) {
        [tmpNode] = tmpNode.children;
      }
      return tmpNode;
    }
    throw new Error('List needs to contain at least one node');
  }

  isSelected(parentValue) {
    return ko.computed(() => {
      const node = this.findNode(this.data, parentValue);
      return node.selected();
    });
  }
}
